import React, { useEffect, useRef, useState } from 'react';
import '../Css/about.css';

const Timeline = () => {
  const timelineRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const maxVisibleItems = isMobile ? 1 : 3; // 1 tarjeta en móvil, 3 en desktop
  
  // Datos de la línea del tiempo
  const timelineEvents = [
    {
      date: 'Octubre 2023',
      description: 'El 15 de octubre de 2023 iniciamos nuestras labores en la ciudad de Trujillo, colaborando con alumnos y colegas en la elaboración de reportes Turnitin y el desarrollo del capítulo de discusión de tesis en etapa final. Esta primera experiencia nos permitió comprender en mayor profundidad las necesidades de los tesistas, así como los desafíos particulares que enfrenta cada investigación.'
    },
    {
      date: 'Abril 2024',
      description: 'Luego de varios meses de experiencia y aprendizaje, concretamos un convenio con una reconocida universidad, con el objetivo de captar tesistas del nivel de pregrado. Este acuerdo nos permitió ampliar nuestra red de clientes y consolidar nuestro enfoque en la asesoría académica especializada.'
    },
    {
      date: 'Mayo 2024',
      description: 'Ante el crecimiento de nuestras operaciones y el impacto positivo generado en la comunidad estudiantil, decidimos formalizar nuestra empresa e inscribirla en los Registros Públicos. Esta acción marcó el inicio de una nueva etapa, caracterizada por una mayor responsabilidad institucional y un creciente reconocimiento en el sector.'
    },
    {
      date: 'Diciembre 2024',
      description: 'Con el propósito de fortalecer la estructura y organización de la empresa, suscribimos un convenio de pasantía con la Universidad Privada del Norte (UPN). Gracias a este acuerdo de carácter trimestral, recibimos practicantes en diversas áreas estratégicas, lo que nos ha permitido optimizar nuestros procesos internos y asegurar una mayor calidad en la prestación de nuestros servicios.'
    },
    {
      date: '2025',
      description: 'A medida que la empresa continuaba su crecimiento, identificamos la necesidad de ampliar nuestro organigrama. Para el año 2025, incorporamos nuevos puestos estratégicos, tales como investigador, estadístico y un área especializada en recursos humanos. Esta reestructuración nos permitió establecer procesos más definidos y alcanzar mejores resultados en la gestión y desarrollo de nuestros servicios.'
    },
  ];

  // Detectar si es móvil
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    
    // Verificar al cargar y en cambios de tamaño
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Controlamos la navegación
  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      
      // Animación al navegar
      if (timelineRef.current) {
        timelineRef.current.style.transition = 'transform 0.5s ease';
        timelineRef.current.style.transform = 'translateX(10px)';
        setTimeout(() => {
          if (timelineRef.current) {
            timelineRef.current.style.transform = 'translateX(0)';
          }
        }, 500);
      }
    }
  };

  const handleNext = () => {
    // En móviles podemos navegar hasta el último elemento
    const maxIndex = isMobile ? 
      timelineEvents.length - maxVisibleItems : // Para móviles: navegar por todas las tarjetas
      timelineEvents.length - maxVisibleItems;  // Para desktop: mantener comportamiento original
      
    if (currentIndex < maxIndex) {
      setCurrentIndex(prev => prev + 1);
      
      // Animación al navegar
      if (timelineRef.current) {
        timelineRef.current.style.transition = 'transform 0.5s ease';
        timelineRef.current.style.transform = 'translateX(-10px)';
        setTimeout(() => {
          if (timelineRef.current) {
            timelineRef.current.style.transform = 'translateX(0)';
          }
        }, 500);
      }
    }
  };

  // Manejar teclas de navegación
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentIndex]);

  return (
    <div className="timeline-container">
      <section id="timeline" ref={timelineRef}>
        {isMobile ? (
          // Versión móvil: solo muestra la tarjeta actual
          <div 
            key={currentIndex}
            className="tl-item"
            style={{ width: '100%' }}
          >
            <div className="tl-bg"></div>
            <div className="tl-year">
              <p className="f2 heading--sanSerif">{timelineEvents[currentIndex].date}</p>
            </div>
            <div className="tl-content">
              <p>{timelineEvents[currentIndex].description}</p>
            </div>
          </div>
        ) : (
          // Versión desktop: muestra 3 tarjetas
          timelineEvents.slice(currentIndex, currentIndex + maxVisibleItems).map((event, index) => (
            <div 
              key={index + currentIndex}
              className="tl-item"
              style={{
                width: `${100 / Math.min(maxVisibleItems, timelineEvents.length - currentIndex)}%`
              }}
            >
              <div className="tl-bg"></div>
              <div className="tl-year">
                <p className="f2 heading--sanSerif">{event.date}</p>
              </div>
              <div className="tl-content">
                <p>{event.description}</p>
              </div>
            </div>
          ))
        )}
      </section>
      
      <div className="timeline-nav">
        <button 
          id="prev-btn" 
          onClick={handlePrev}
          disabled={currentIndex === 0}
          aria-label="Anterior"
        >
          ←
        </button>
        <button 
          id="next-btn" 
          onClick={handleNext}
          disabled={isMobile ? 
            currentIndex >= timelineEvents.length - 1 : // Para móviles: hasta el último
            currentIndex >= timelineEvents.length - maxVisibleItems // Para desktop: original
          }
          aria-label="Siguiente"
        >
          → 
        </button>
      </div>
    </div>
  );
};

export default Timeline;